import type { QuestScope } from '@aegislab/shared';
import { useWindowStore } from '@/shared/model';

/** 착지 윈도우 선택 옵션. */
export interface QuestDeepLinkOptions {
  /**
   * 팀 퀘스트를 **제품 축**(개발 퀘스트 창)에서 볼 것인지. DEV capability 팀의 진입점이 세운다.
   *
   * 창을 가르는 축이 `QuestScope` 하나가 아니라 둘인 이유: 개발 퀘스트 창은 같은 TEAM 퀘스트를
   * 사내 제품으로 묶어 볼 뿐이라 스코프로는 팀 퀘스트 창과 구별되지 않는다(windowRegistry
   * 'dev-quests' 주석). 그래서 목적지는 호출자가 밝히고, 어느 제품 탭에 착지할지는
   * consumer(`useQuestDeepLink`)가 target 퀘스트의 productId 로 역도출한다.
   */
  productAxis?: boolean;
}

/**
 * 퀘스트 scope → 착지 윈도우 매핑 (창 선택 단일 출처).
 * GLOBAL(월드)만 'world-quests' 윈도우, 그 외(TEAM·SYSTEM)는 'quests' 윈도우.
 * `productAxis` 가 켜지면 팀 퀘스트의 목적지가 개발 퀘스트 창('dev-quests')으로 바뀐다 —
 * 월드 퀘스트에는 제품 축 자체가 없으므로 GLOBAL 은 옵션과 무관하게 'world-quests' 다.
 *
 * `QuestScope`는 `@aegislab/shared`의 3값(TEAM·GLOBAL·SYSTEM)을 쓴다 — frontend 로컬
 * QuestScope(useQuestOrchestrator = TEAM·GLOBAL 2값)로 좁히면 SYSTEM 분기가 사라진다.
 */
export function questWindowFor(
  scope: QuestScope,
  options?: QuestDeepLinkOptions
): 'quests' | 'world-quests' | 'dev-quests' {
  if (scope === 'GLOBAL') return 'world-quests';
  return options?.productAxis ? 'dev-quests' : 'quests';
}

/** 퀘스트가 어느 축에 매여 있는지 판정하는 데 필요한 최소 형태. */
interface QuestAxisFields {
  /** 시나리오 직속(광고주 캠페인 퀘스트). */
  scenarioId?: string | null;
  /** 시나리오발 요청(애드코드·VA)이 출처 시나리오를 park 해 두는 칸. */
  requesterScenarioId?: string | null;
  /** 제품 축(개발 퀘스트 창)의 착지 탭. null·미전달이면 제품 미지정 탭이 목적지다. */
  productId?: string | null;
}

/**
 * 이 퀘스트가 **개발 퀘스트 창(제품 축)에 자리가 있는지**.
 *
 * 창을 사람이 아니라 카드로 가르는 지점이다. DEV capability 팀 사람도 팀 퀘스트(시나리오)와
 * 월드 퀘스트를 함께 진행하므로, "이 사람이 개발팀이다"만으로 목적지를 정하면 시나리오 퀘스트가
 * 시나리오 스파인 없는 창으로 끌려가 광고주·라운드 맥락을 잃는다.
 *
 * 제외 술어를 origin 열거(`'ADCODE_REQUEST' | 'VA_REQUEST' | …`)가 아니라 **시나리오 매임**으로
 * 쓴 이유: 애드코드·VA 는 둘 다 시나리오발 요청이라 출처를 `requesterScenarioId` 에 park 한다.
 * origin 을 나열하면 요청 종류가 늘 때마다 여기가 조용히 낡는다. 특히 애드코드는 제품 축에
 * **탭 자체가 없다**(`QuestListPanel` scopedQuests: "애드코드는 시나리오발 요청이라 개발 퀘스트
 * 창에 자리가 없다") — 보내면 착지할 탭이 없다.
 *
 * 남는 것(두 축 모두 비어 있는 팀 퀘스트)은 개발팀 돌발 유입이라 제품 미지정 탭이 제자리다.
 * 제품·시나리오가 배타적인 두 축인 건 스키마 결정이다(`product.prisma`: "시나리오는 이 아래로
 * 들어가지 않는다").
 */
export function landsOnProductAxis(quest: QuestAxisFields, hasDevQuestWindow: boolean): boolean {
  if (!hasDevQuestWindow) return false;
  return quest.scenarioId == null && quest.requesterScenarioId == null;
}

/**
 * 퀘스트 상세(증빙 폼)로 점프하는 딥링크를 연다.
 *
 * 창 선택은 `quest.scope` + 위 `landsOnProductAxis` 로 결정한다(questWindowFor). 시나리오 스파인
 * 정렬은 consumer(`useQuestDeepLink`)가 questId로 역도출한다.
 *
 * **제품 축만은 여기서 밝힌다** — 역도출은 대상이 그 창의 목록에 있어야 되는데, 쓰던 창은
 * 업무명 검색어가 남아 있으면 목록이 서버에서 이미 좁혀져 와 대상이 거기 없다. 누르는 쪽은
 * 그 퀘스트를 손에 쥐고 있으니, 눌린 시점의 제품을 실어 보내 목록과 무관하게 탭을 맞춘다.
 */
export function openQuestDeepLink(
  quest: { id: string; scope: QuestScope } & QuestAxisFields,
  options?: { hasDevQuestWindow?: boolean; focus?: boolean }
): void {
  const productAxis = landsOnProductAxis(quest, options?.hasDevQuestWindow ?? false);
  const target = questWindowFor(quest.scope, { productAxis });
  const source = options?.focus ? document.activeElement : null;
  useWindowStore.getState().openWindow(target, {
    questId: quest.id,
    // 제품 미지정(null)은 칸을 비워 보낸다 — 소비처가 "제품 없음"을 미지정 탭으로 읽는다.
    ...(productAxis && quest.productId ? { productId: quest.productId } : {}),
  });
  if (options?.focus) {
    requestAnimationFrame(() => {
      const state = useWindowStore.getState();
      if (document.activeElement !== source || state.focusedWindow !== target) return;
      if (!state.openWindows.includes(target) || state.minimizedWindows.includes(target)) return;
      document
        .querySelector<HTMLElement>(`[data-window-frame][data-window-id="${target}"]`)
        ?.focus({ preventScroll: true });
    });
  }
}
